import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { AlertCircle, Check, RefreshCw } from 'lucide-react';
import PermissionsAssignmentModal from '../../common/Permissions/PermissionsAssignmentModal';

// Type Definitions
export type Theme = 'dark' | 'light';

export interface ChatAgentSecurityProps {
  theme?: Theme;
  objectId?: string;
  moduleId?: string;
  entityName?: string;
  hideHeader?: boolean;
}

// Theme Configuration
const THEME_CONFIG = {
  dark: {
    primary: 'bg-neutral-900',
    secondary: 'bg-neutral-800',
    background: 'bg-neutral-950',
    surface: 'bg-neutral-800',
    border: 'border-neutral-700',
    text: {
      primary: 'text-white',
      secondary: 'text-neutral-300',
      muted: 'text-neutral-400'
    },
    accent: 'bg-indigo-600',
    input: 'bg-neutral-700 border-neutral-600 text-white'
  },
  light: {
    primary: 'bg-white',
    secondary: 'bg-gray-50',
    background: 'bg-gray-100',
    surface: 'bg-white',
    border: 'border-gray-200',
    text: {
      primary: 'text-gray-900',
      secondary: 'text-gray-800',
      muted: 'text-gray-700'
    },
    accent: 'bg-indigo-600',
    input: 'bg-white border-gray-300 text-gray-900'
  }
};

const CHAT_AGENT_MODULE_ID = 'd4c3b2a1-5e6f-4d3c-9b2a-3c4d5e6f7a8b';

const ChatAgentSecurity: React.FC<ChatAgentSecurityProps> = ({
  theme = 'dark',
  objectId,
  moduleId,
  entityName = 'Chat Agent',
  hideHeader = false,
}) => {
  const { chatbotId } = useParams<{ chatbotId?: string }>();
  const agentId = objectId || chatbotId || '';
  const colors = THEME_CONFIG[theme];

  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [refreshSeed, setRefreshSeed] = useState(0);

  const handleRefresh = () => setRefreshSeed(s => s + 1);

  return (
    <div className={`h-full overflow-auto flex flex-col ${hideHeader ? '' : colors.background}`}>
      {!hideHeader ? (
      <div className={`px-6 py-4 border-b ${colors.border} ${colors.primary} flex-shrink-0`}>
        <div className="flex items-center justify-between">
          <div>
            <h1 className={`text-lg font-semibold ${colors.text.primary}`}>Security & Access</h1>
            <p className={`text-sm ${colors.text.muted} mt-1`}>
              Manage permissions for this {entityName.toLowerCase()}
            </p>
          </div>
          <div className="flex items-center gap-3">
            {successMessage && (
              <div className="flex items-center gap-2 text-green-500 text-sm">
                <Check className="w-4 h-4" />
                {successMessage}
              </div>
            )}
            <button
              onClick={handleRefresh}
              className={`px-3 py-2 ${colors.secondary} border ${colors.border} hover:bg-neutral-700 text-sm rounded-md transition-colors flex items-center gap-2`}
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>
      </div>
      ) : null}

      {/* Error Message */}
      {error && (
        <div className={`mx-6 mt-4 p-4 ${theme === 'light' ? 'bg-red-50 border-red-200' : 'bg-red-900/20 border-red-700'} border rounded-md flex items-start gap-3`}>
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className={`text-sm font-medium ${theme === 'light' ? 'text-red-800' : 'text-red-200'}`}>Error</p>
            <p className={`text-xs ${theme === 'light' ? 'text-red-700' : 'text-red-300'} mt-1`}>{error}</p>
          </div>
        </div>
      )}

      {/* Embedded Manage Permissions */}
      <div className={`flex-1 min-h-0 flex flex-col ${hideHeader ? 'p-4' : 'p-6'}`}>
        <PermissionsAssignmentModal
          key={`security-${agentId}-${refreshSeed}`}
          entityType="chat-agent"
          entityName={entityName}
          embedded
          module_id={moduleId || CHAT_AGENT_MODULE_ID}
          object_id={agentId}
          onClose={() => {}}
          onSave={() => {
            setSuccessMessage('Permissions updated.');
            setTimeout(() => setSuccessMessage(null), 3000);
          }}
        />
      </div>
    </div>
  );
};

export default ChatAgentSecurity;  